import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, 
  Calendar,
  Beaker,
  Camera,
  FileText,
  Sparkles,
  Crown,
  Clock,
  ChevronRight,
  Trash2,
  Edit,
  Eye,
  ArrowUpDown,
  Droplets,
  Scissors,
  Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const TREATMENT_TYPES = [
  { value: "haircut", label: "Haircut", icon: "✂️" },
  { value: "color", label: "Color Treatment", icon: "🎨" },
  { value: "highlights", label: "Highlights/Lowlights", icon: "✨" },
  { value: "relaxer", label: "Relaxer/Texturizer", icon: "💆" },
  { value: "keratin", label: "Keratin Treatment", icon: "💎" },
  { value: "scalp_treatment", label: "Scalp Treatment", icon: "🔬" },
  { value: "deep_condition", label: "Deep Conditioning", icon: "💧" },
  { value: "perm", label: "Perm/Curl", icon: "🌀" },
  { value: "extension", label: "Extensions", icon: "💇" },
  { value: "other", label: "Other", icon: "📝" }
];

const ScalpCareLog = ({ clientId, clientName, isPremium = false }) => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [formData, setFormData] = useState({
    visit_date: new Date().toISOString().split("T")[0],
    treatment_type: "",
    products_used: "",
    color_formula: "",
    ph_level_before: "",
    ph_level_after: "",
    scalp_condition_notes: "",
    stylist_notes: "",
    recommendations: "",
    next_treatment_date: ""
  });

  useEffect(() => {
    if (clientId && isPremium) {
      fetchLogs();
    }
  }, [clientId, isPremium]);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/api/scalp-care/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Scalp Care Log is a Premium feature. Upgrade to Pro or Elite.");
      } else {
        console.error("Error fetching scalp care logs:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        client_id: clientId,
        visit_date: formData.visit_date,
        treatment_type: formData.treatment_type,
        products_used: formData.products_used.split(",").map(p => p.trim()).filter(Boolean),
        color_formula: formData.color_formula || null,
        ph_level_before: formData.ph_level_before ? parseFloat(formData.ph_level_before) : null,
        ph_level_after: formData.ph_level_after ? parseFloat(formData.ph_level_after) : null,
        scalp_condition_notes: formData.scalp_condition_notes || null,
        stylist_notes: formData.stylist_notes || null,
        recommendations: formData.recommendations || null,
        next_treatment_date: formData.next_treatment_date || null,
        photos: []
      };

      if (selectedLog) {
        await axios.put(`${API}/api/scalp-care/${selectedLog.log_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Scalp care log updated!");
      } else {
        await axios.post(`${API}/api/scalp-care/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Scalp care log created!");
      }

      setShowAddModal(false);
      setSelectedLog(null);
      resetForm();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save scalp care log");
    }
  };

  const handleDelete = async (logId) => {
    if (!confirm("Are you sure you want to delete this log?")) return;
    
    try {
      await axios.delete(`${API}/api/scalp-care/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Log deleted");
      fetchLogs();
    } catch (error) {
      toast.error("Failed to delete log");
    }
  };

  const resetForm = () => {
    setFormData({
      visit_date: new Date().toISOString().split("T")[0],
      treatment_type: "",
      products_used: "",
      color_formula: "",
      ph_level_before: "",
      ph_level_after: "",
      scalp_condition_notes: "",
      stylist_notes: "",
      recommendations: "",
      next_treatment_date: ""
    });
  };

  const editLog = (log) => {
    setSelectedLog(log);
    setFormData({
      visit_date: log.visit_date || "",
      treatment_type: log.treatment_type || "",
      products_used: log.products_used?.join(", ") || "",
      color_formula: log.color_formula || "",
      ph_level_before: log.ph_level_before?.toString() || "",
      ph_level_after: log.ph_level_after?.toString() || "",
      scalp_condition_notes: log.scalp_condition_notes || "",
      stylist_notes: log.stylist_notes || "",
      recommendations: log.recommendations || "",
      next_treatment_date: log.next_treatment_date || ""
    });
    setShowAddModal(true);
  };

  const getTreatmentInfo = (type) => {
    return TREATMENT_TYPES.find(t => t.value === type) || { label: type, icon: "📝" };
  };

  // Premium Gate
  if (!isPremium) {
    return (
      <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold mb-4">
            <Crown className="w-3 h-3 mr-1" />
            Pro/Elite Feature
          </Badge>
          <h3 className="text-xl font-bold text-white mb-2">Scalp Care Log</h3>
          <p className="text-gray-400 mb-4">
            Track treatments, pH levels, and client progress over time with detailed professional notes.
          </p>
          <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black">
            Upgrade to Unlock
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Beaker className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Scalp Care Log</h3>
            <p className="text-sm text-gray-400">{clientName} • {logs.length} records</p>
          </div>
        </div>
        <Dialog open={showAddModal} onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) {
            setSelectedLog(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Beaker className="w-5 h-5 text-teal-400" />
                {selectedLog ? "Edit Scalp Care Entry" : "New Scalp Care Entry"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Date & Treatment Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Visit Date *</Label>
                  <Input
                    type="date"
                    value={formData.visit_date}
                    onChange={(e) => setFormData({...formData, visit_date: e.target.value})}
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Treatment Type *</Label>
                  <Select
                    value={formData.treatment_type}
                    onValueChange={(value) => setFormData({...formData, treatment_type: value})}
                  >
                    <SelectTrigger className="bg-stone-800 border-stone-600 text-white mt-1">
                      <SelectValue placeholder="Select treatment" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-800 border-stone-600">
                      {TREATMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-white hover:bg-stone-700">
                          <span>{type.icon} {type.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* pH Levels */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-blue-400" />
                    pH Level Before (0-14)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="14"
                    step="0.1"
                    value={formData.ph_level_before}
                    onChange={(e) => setFormData({...formData, ph_level_before: e.target.value})}
                    placeholder="e.g., 5.5"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-emerald-400" />
                    pH Level After (0-14)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="14"
                    step="0.1"
                    value={formData.ph_level_after}
                    onChange={(e) => setFormData({...formData, ph_level_after: e.target.value})}
                    placeholder="e.g., 4.5"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>
              </div>

              {/* Products & Color Formula */}
              <div>
                <Label className="text-gray-300">Products Used (comma separated)</Label>
                <Input
                  value={formData.products_used}
                  onChange={(e) => setFormData({...formData, products_used: e.target.value})}
                  placeholder="e.g., Olaplex No.3, Color Wow, DevaCurl"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-gray-300">Color Formula (if applicable)</Label>
                <Input
                  value={formData.color_formula}
                  onChange={(e) => setFormData({...formData, color_formula: e.target.value})}
                  placeholder="e.g., 7N + 8G (1:1) with 20vol"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>

              {/* Notes */}
              <div>
                <Label className="text-gray-300">Scalp Condition Notes</Label>
                <Textarea
                  value={formData.scalp_condition_notes}
                  onChange={(e) => setFormData({...formData, scalp_condition_notes: e.target.value})}
                  placeholder="Note any scalp conditions observed..."
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-gray-300">Stylist Notes</Label>
                <Textarea
                  value={formData.stylist_notes}
                  onChange={(e) => setFormData({...formData, stylist_notes: e.target.value})}
                  placeholder="Treatment notes, observations, techniques used..."
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-gray-300">Recommendations for Client</Label>
                <Textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({...formData, recommendations: e.target.value})}
                  placeholder="Home care recommendations, products to use..."
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                  rows={2}
                />
              </div>

              {/* Next Treatment */}
              <div>
                <Label className="text-gray-300">Next Treatment Date (suggested)</Label>
                <Input
                  type="date"
                  value={formData.next_treatment_date}
                  onChange={(e) => setFormData({...formData, next_treatment_date: e.target.value})}
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border-stone-600 text-gray-300 hover:bg-stone-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
                >
                  {selectedLog ? "Update Entry" : "Save Entry"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : logs.length === 0 ? (
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="p-8 text-center">
            <Beaker className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No scalp care logs yet.</p>
            <p className="text-gray-500 text-sm">Start tracking treatments and progress.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const treatment = getTreatmentInfo(log.treatment_type);
            return (
              <Card key={log.log_id} className="bg-stone-800/50 border-stone-700 hover:border-teal-500/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center text-xl">
                        {treatment.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">{treatment.label}</h4>
                          <Badge variant="outline" className="text-xs border-teal-500/30 text-teal-400">
                            {new Date(log.visit_date).toLocaleDateString()}
                          </Badge>
                        </div>
                        {log.products_used?.length > 0 && (
                          <p className="text-sm text-gray-400 mt-1">
                            Products: {log.products_used.slice(0, 3).join(", ")}
                            {log.products_used.length > 3 && ` +${log.products_used.length - 3} more`}
                          </p>
                        )}
                        {(log.ph_level_before || log.ph_level_after) && (
                          <div className="flex items-center gap-3 mt-2">
                            {log.ph_level_before && (
                              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                                pH Before: {log.ph_level_before}
                              </span>
                            )}
                            {log.ph_level_after && (
                              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                                pH After: {log.ph_level_after}
                              </span>
                            )}
                          </div>
                        )}
                        {log.color_formula && (
                          <p className="text-xs text-amber-400 mt-1">
                            Formula: {log.color_formula}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => editLog(log)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(log.log_id)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScalpCareLog;
